import { useToastContext } from '../contexts/ToastContext';

/**
 * Custom hook for sharing functionality
 * Supports Web Share API (mobile) and clipboard fallback (desktop)
 * 
 * @param {Object} options - Share options
 * @param {string} options.title - Title for the share
 * @param {string} options.text - Text description for the share
 * @param {string} options.url - URL to share (defaults to current page URL)
 * @returns {Function} handleShare - Function to trigger sharing
 */
const useShare = ({ title, text, url = window.location.href } = {}) => {
  const { success } = useToastContext();

  /**
   * Copy link to clipboard with fallback for older browsers
   */
  const handleCopyToClipboard = async (linkUrl = url) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      success('Link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = linkUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        success('Link copied to clipboard!');
      } catch (fallbackErr) {
        console.error('Failed to copy:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  /**
   * Handle share functionality
   * Tries Web Share API first, falls back to clipboard
   */
  const handleShare = async (customOptions = {}) => {
    const shareData = {
      title: customOptions.title || title || 'Share',
      text: customOptions.text || text || 'Check this out!',
      url: customOptions.url || url,
    };

    // Try Web Share API first (mobile browsers)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        // User cancelled or error occurred, fallback to clipboard
        if (err.name !== 'AbortError') {
          handleCopyToClipboard(shareData.url);
        }
      }
    } else {
      // Fallback to clipboard for desktop browsers
      handleCopyToClipboard(shareData.url);
    }
  };

  return { handleShare, handleCopyToClipboard };
};

export default useShare;

